import type { ModuleId, SessionMode } from '../core/types';
import {
  createCentralPeripheralStimulus,
} from './modules/central-peripheral';
import {
  createMovingTargetStimulus,
  type GapDirection,
  type MovingTargetStartSide,
} from './modules/moving-target';
import { createPlayerTrackTargetIds } from './modules/player-track';
import {
  createVisualSweepStimulus,
  type SweepDirection,
  type SweepOrientation,
} from './modules/read-switch';

export interface TrialSpecification {
  seed: number;
  difficulty: number;
}

const MODE_BASE_SEEDS: Record<SessionMode, number> = {
  familiarization: 11_000,
  'baseline-a': 101_000,
  'baseline-b': 211_000,
  training: 401_000,
  reassessment: 701_000,
  retention: 901_000,
};

const MODULE_OFFSET: Record<ModuleId, number> = {
  'read-switch': 0,
  'central-peripheral': 10_000_000,
  'player-track': 20_000_000,
  'moving-target': 30_000_000,
};

const ASSESSMENT_MODES: readonly SessionMode[] = [
  'baseline-a',
  'baseline-b',
  'reassessment',
  'retention',
];

function stableStringHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function findSeed(
  start: number,
  used: Set<number>,
  predicate: (seed: number) => boolean,
): number {
  for (let offset = 0; offset < 50_000; offset += 1) {
    const seed = start + offset;
    if (!used.has(seed) && predicate(seed)) {
      used.add(seed);
      return seed;
    }
  }
  throw new Error(`Unable to build a balanced fixed form from seed ${start}`);
}

function fixedDifficulty(moduleId: ModuleId, index: number): number {
  if (moduleId === 'player-track') {
    const position = index % 8;
    const repetition = Math.floor(index / 8);
    return [1, 3, 5][(position + repetition) % 3] ?? 3;
  }
  const levelIndex = moduleId === 'moving-target'
    ? (Math.floor(index / 4) + (index % 4)) % 4
    : moduleId === 'central-peripheral'
      ? ((index % 8) + Math.floor(index / 8)) % 4
      : (Math.floor(index / 4) + 2 * (index % 4)) % 4;
  return [1.5, 2.5, 3.5, 4.5][levelIndex] ?? 3;
}

function centralSymbolForIndex(index: number): 'vertical' | 'horizontal' {
  const position = index % 8;
  const repetition = Math.floor(index / 8);
  return (position + Math.floor(repetition / 2)) % 2 === 0 ? 'vertical' : 'horizontal';
}

function buildAssessmentSeeds(
  moduleId: ModuleId,
  mode: SessionMode,
  count: number,
): number[] {
  const base = MODE_BASE_SEEDS[mode] + MODULE_OFFSET[moduleId];
  const used = new Set<number>();
  return Array.from({ length: count }, (_, index) => {
    const start = base + index * 50_000;
    if (moduleId === 'central-peripheral') {
      const peripheralPosition = (index % 8) + 1;
      const centralSymbol = centralSymbolForIndex(index);
      return findSeed(start, used, (seed) => {
        const stimulus = createCentralPeripheralStimulus(seed, fixedDifficulty(moduleId, index));
        return stimulus.peripheralPosition === peripheralPosition
          && stimulus.centralSymbol === centralSymbol;
      });
    }
    if (moduleId === 'moving-target') {
      const gap = (['up', 'right', 'down', 'left'] as const)[index % 4] as GapDirection;
      const startSide = (['left', 'right', 'up', 'down'] as const)[
        (index + Math.floor(index / 4)) % 4
      ] as MovingTargetStartSide;
      return findSeed(start, used, (seed) => {
        const stimulus = createMovingTargetStimulus(seed, fixedDifficulty(moduleId, index));
        return stimulus.gapDirection === gap && stimulus.startSide === startSide;
      });
    }
    if (moduleId === 'read-switch') {
      const pairs: Array<[SweepDirection, SweepDirection]> = [
        ['contracting', 'contracting'], ['contracting', 'expanding'],
        ['expanding', 'contracting'], ['expanding', 'expanding'],
      ];
      const orientations: SweepOrientation[] = ['vertical', 'horizontal', 'diagonal-up', 'diagonal-down'];
      const desiredPair = pairs[index % pairs.length] as [SweepDirection, SweepDirection];
      const desiredOrientation = orientations[(index % pairs.length + Math.floor(index / pairs.length)) % orientations.length] as SweepOrientation;
      return findSeed(start, used, (seed) => {
        const stimulus = createVisualSweepStimulus(seed, fixedDifficulty(moduleId, index));
        return stimulus.directions[0] === desiredPair[0]
          && stimulus.directions[1] === desiredPair[1]
          && stimulus.orientation === desiredOrientation;
      });
    }
    const targetStart = (index + Math.floor(index / 8) * 2) % 8;
    const desiredTargets = [targetStart, targetStart + 1, targetStart + 3, targetStart + 5]
      .map((value) => value % 8 + 1)
      .sort((left, right) => left - right);
    return findSeed(start, used, (seed) =>
      createPlayerTrackTargetIds(seed).every((target, targetIndex) => target === desiredTargets[targetIndex]),
    );
  });
}

export function buildTrialSpecifications(
  moduleId: ModuleId,
  mode: SessionMode,
  count: number,
  sessionId: string,
): TrialSpecification[] {
  if (!Number.isInteger(count) || count <= 0) throw new RangeError('trial count must be positive');
  if (ASSESSMENT_MODES.includes(mode)) {
    return buildAssessmentSeeds(moduleId, mode, count).map((seed, index) => ({
      seed,
      difficulty: fixedDifficulty(moduleId, index),
    }));
  }
  const sessionOffset = stableStringHash(sessionId) % 100_000_000;
  const base = MODE_BASE_SEEDS[mode] + MODULE_OFFSET[moduleId] + sessionOffset;
  if (moduleId === 'central-peripheral') {
    const used = new Set<number>();
    return Array.from({ length: count }, (_, index) => {
      const peripheralPosition = (index % 8) + 1;
      const centralSymbol = centralSymbolForIndex(index);
      return {
        seed: findSeed(base + index * 50_000, used, (seed) => {
          const stimulus = createCentralPeripheralStimulus(seed, 1);
          return stimulus.peripheralPosition === peripheralPosition
            && stimulus.centralSymbol === centralSymbol;
        }),
        difficulty: 1,
      };
    });
  }
  return Array.from({ length: count }, (_, index) => ({
    seed: base + index * 97 + 17,
    difficulty: mode === 'familiarization' ? 1.5 : 3,
  }));
}
